import React, { useState } from "react";
import {
  hasFieldError,
  getFieldErrorMessage,
  getInputStyles,
  getLabelStyles,
  getLabelText,
} from "../utils/validation";
import {
  Stack,
  Group,
  Text,
  ActionIcon,
  Paper,
  Grid,
  Box,
  TextInput,
} from "@mantine/core";
import { IconChevronLeft, IconTrash, IconCheck } from "@tabler/icons-react";
import { notifyError, notifySuccess } from "../../../utils/notify";
import { useApp } from "../../../context/AppContext";
import { useSalon } from "../../../context/SalonContext";
import { BusinessInfo } from "../../../types/context";

const COLOR_BORDER_LIGHT = "#e9ecef";
const COLOR_BORDER_SELECTED = "#228be6";
const COLOR_BACKGROUND_LIGHT = "#f8f9fa";

interface Photo {
  id?: number | string;
  filename?: string;
  selected?: boolean;
  url?: string;
  src?: string;
  [key: string]: any;
}

interface GalleryContent {
  title?: string;
  items?: Photo[];
  gridLayout?: string;
}

interface WebsiteGeneratorGalleryProps {
  onBack?: () => void;
  images?: Photo[];
  selectedImages?: Photo[];
  onImageSelect?: (image: Photo) => void;
  onImageDelete?: (image: Photo) => void;
  onImageEdit?: (image: Photo) => void;
  onSeeMore?: () => void;
  missingFields?: string[];
  requiredValidation?: { [field: string]: boolean };
  galleryContent?: GalleryContent; // Full gallery content object (includes title)
  onGalleryContentChange?: (content: GalleryContent) => void; // Handler for full content changes
}

const WebsiteGeneratorGallery: React.FC<WebsiteGeneratorGalleryProps> = ({
  onBack,
  images,
  selectedImages,
  onImageSelect,
  onImageDelete,
  onSeeMore,
  missingFields = [],
  requiredValidation = {},
  galleryContent,
  onGalleryContentChange,
}) => {
  const { deleteImage } = useApp();
  const { businessInfo } = useSalon() as { businessInfo?: BusinessInfo | null };
  const [photos, setPhotos] = useState<Photo[]>([]);

  const handleTitleChange = (value: string) => {
    if (onGalleryContentChange) {
      // Always create/update the gallery content object, even if galleryContent is undefined
      onGalleryContentChange({
        ...(galleryContent || {}),
        title: value,
        items: galleryContent?.items || [],
        gridLayout: galleryContent?.gridLayout,
      });
    }
  };

  const handlePhotoSelect = (id: number | string | undefined) => {
    if (!id) return;
    if (onImageSelect && images && selectedImages) {
      const photo = images.find((p) => p.id === id);
      if (photo) onImageSelect(photo);
    } else {
      setPhotos(
        photos.map((photo) =>
          photo.id === id ? { ...photo, selected: !photo.selected } : photo,
        ),
      );
    }
  };

  const handlePhotoDelete = async (id: number | string | undefined) => {
    if (!id) return;

    // Find the photo to delete
    const photoToDelete =
      images?.find((p) => p.id === id) || photos.find((p) => p.id === id);
    if (!photoToDelete || !photoToDelete.filename) return;

    // Always confirm before any deletion (callback or internal API call)
    if (
      !window.confirm(
        `Are you sure you want to delete ${photoToDelete.filename}?`,
      )
    ) {
      return;
    }

    // If onImageDelete callback is provided, call it after confirmation
    if (onImageDelete && images) {
      onImageDelete(photoToDelete);
      return;
    }

    // Otherwise, handle API call here
    if (businessInfo?.id && photoToDelete.filename) {
      try {
        await deleteImage({
          salonId: businessInfo.id,
          filename: photoToDelete.filename,
          delete_thumbnail: true,
        });

        // Remove from local state
        setPhotos(photos.filter((photo) => photo.id !== id));

        notifySuccess("Image deleted successfully", {
          title: "Success",
          autoClose: 3000,
        });
      } catch (error) {
        console.error("Error deleting image:", error);
        notifyError("Failed to delete image. Please try again.", {
          title: "Error",
          autoClose: 5000,
        });
      }
    } else {
      // Fallback to local state removal if no API available
      setPhotos(photos.filter((photo) => photo.id !== id));
    }
  };

  const displayPhotos = images || photos;

  // Determine if a photo is selected by checking if it's in selectedImages array
  const isPhotoSelected = (photo: Photo): boolean => {
    if (selectedImages && selectedImages.length > 0) {
      return selectedImages.some((selected) => selected.id === photo.id);
    }
    return photo.selected === true;
  };

  const selectedCount = displayPhotos.filter((p) => isPhotoSelected(p)).length;

  // Check if gallery items field has error
  const currentItemsValue =
    galleryContent?.items && galleryContent.items.length > 0
      ? galleryContent.items
      : selectedImages;

  const hasItemsError =
    hasFieldError("items", requiredValidation, currentItemsValue) ||
    missingFields.includes("gallery.items");

  return (
    <Stack gap="md">
      {/* Back Navigation */}
      {onBack && (
        <Group justify="space-between" align="center">
          <ActionIcon variant="subtle" onClick={onBack}>
            <IconChevronLeft size={16} />
          </ActionIcon>
          <Text fw={600} size="lg">
            Gallery
          </Text>
        </Group>
      )}
      {hasItemsError && (
        <Text size="sm" c="red">
          At least one gallery item is required
        </Text>
      )}

      {/* Gallery Title Input */}
      {(galleryContent !== undefined || onGalleryContentChange) && (
        <Box>
          <Text
            size="sm"
            fw={500}
            mb="xs"
            style={getLabelStyles(
              "title",
              requiredValidation,
              galleryContent?.title,
            )}
          >
            {getLabelText("Gallery Title", "title", requiredValidation)}
          </Text>
          <TextInput
            placeholder="Gallery"
            value={galleryContent?.title || ""}
            onChange={(event) => handleTitleChange(event.currentTarget.value)}
            error={getFieldErrorMessage(
              "title",
              requiredValidation,
              {},
              galleryContent?.title,
            )}
            maxLength={100}
            styles={getInputStyles(
              "title",
              requiredValidation,
              missingFields,
              "gallery.title",
              galleryContent?.title,
            )}
          />
        </Box>
      )}

      {/* Gallery Items Header */}
      <Text
        fw={600}
        size="lg"
        style={{ color: hasItemsError ? "red" : undefined }}
      >
        Gallery Items {selectedCount}/10 {hasItemsError && "*"}
      </Text>

      {/* Photos Label */}
      <Text size="sm" fw={500}>
        Photos
      </Text>

      {/* Photo Grid */}
      <Grid gutter="sm">
        {/* See More Button */}
        {onSeeMore && (
          <Grid.Col span={4}>
            <Paper
              p="md"
              style={{
                border: `1px solid ${COLOR_BORDER_LIGHT}`,
                borderRadius: "8px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "120px",
              }}
              onClick={onSeeMore}
            >
              <Text size="sm" c="dimmed">
                See more
              </Text>
            </Paper>
          </Grid.Col>
        )}

        {/* Photo Thumbnails */}
        {displayPhotos.map((photo) => {
          const isSelected = isPhotoSelected(photo);
          return (
            <Grid.Col key={photo.id} span={4}>
              <Paper
                p="xs"
                style={{
                  border: isSelected
                    ? `3px solid ${COLOR_BORDER_SELECTED}`
                    : `1px solid ${COLOR_BORDER_LIGHT}`,
                  borderRadius: "8px",
                  cursor: "pointer",
                  position: "relative",
                }}
                onClick={() => handlePhotoSelect(photo.id)}
                onContextMenu={(e) => {
                  // Prevent right-click menu that might trigger extensions
                  e.preventDefault();
                }}
              >
                {/* Selection Checkmark */}
                {isSelected && (
                  <ActionIcon
                    size="sm"
                    color="blue"
                    style={{ position: "absolute", top: 4, left: 4, zIndex: 1 }}
                  >
                    <IconCheck size={12} />
                  </ActionIcon>
                )}

                {/* Photo Image or Placeholder */}
                <Box
                  style={{
                    width: "100%",
                    height: "100px",
                    backgroundColor: COLOR_BACKGROUND_LIGHT,
                    borderRadius: "4px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                    backgroundImage:
                      photo.url || photo.src
                        ? undefined
                        : "url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBmaWxsPSIjRjVGNUY1Ii8+CjxwYXRoIGQ9Ik0yMCAxMEMyNi42MjcgMTAgMzIgMTUuMzczIDMyIDIyQzMyIDI4LjYyNyAyNi42MjcgMzQgMjAgMzRDMTMuMzczIDM0IDggMjguNjI3IDggMjJDOCAxNS4zNzMgMTMuMzczIDEwIDIwIDEwWiIgZmlsbD0iI0NDQ0NDQyIvPgo8cGF0aCBkPSJNMjAgMTZDMjIuMjA5IDE2IDI0IDE3Ljc5MSAyNCAyMEMyNCAyMi4yMDkgMjIuMjA5IDI0IDIwIDI0QzE3Ljc5MSAyNCAxNiAyMi4yMDkgMTYgMjBDMTYgMTcuNzkxIDE3Ljc5MSAxNiAyMCAxNloiIGZpbGw9IiM5OTk5OTkiLz4KPC9zdmc+Cg==')",
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "center",
                  }}
                >
                  {photo.url || photo.src ? (
                    <img
                      src={photo.url || photo.src}
                      alt={photo.filename || "Image"}
                      loading="lazy"
                      draggable="false"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        pointerEvents: "none",
                        userSelect: "none",
                      }}
                      onError={(e) => {
                        // Hide image on error, show placeholder
                        (e.target as HTMLImageElement).style.display = "none";
                      }}
                      onMouseDown={(e) => {
                        // Prevent browser extensions from intercepting
                        e.preventDefault();
                      }}
                    />
                  ) : null}
                </Box>

                {/* Filename */}
                <Text size="xs" c="dimmed" mt="xs" ta="center">
                  {photo.filename}
                </Text>

                {/* Delete Button */}
                <ActionIcon
                  size="sm"
                  color="red"
                  variant="subtle"
                  style={{ position: "absolute", top: 4, right: 4, zIndex: 1 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePhotoDelete(photo.id);
                  }}
                >
                  <IconTrash size={12} />
                </ActionIcon>
              </Paper>
            </Grid.Col>
          );
        })}
      </Grid>
    </Stack>
  );
};

export default WebsiteGeneratorGallery;
